import React from 'react';
import { Seo } from '../components/Seo';
import { PageHeader } from '../components/PageHeader';
import { useLanguage } from '../contexts/LanguageContext';
import { business } from '../data/business';
import type { L } from '../types/i18n';
interface LegalProps {
  kind: 'privacy' | 'terms';
}
interface LegalPage {
  eyebrow: L;
  title: L;
  description: L;
  sections: {
    heading: L;
    body: L;
  }[];
}
const content: Record<'privacy' | 'terms', LegalPage> = {
  privacy: {
    eyebrow: {
      en: 'Privacy Policy',
      es: 'Política de Privacidad'
    },
    title: {
      en: 'How we handle your information',
      es: 'Cómo manejamos tu información'
    },
    description: {
      en: 'We collect only what we need to service your vehicle and contact you about it.',
      es: 'Solo recopilamos lo necesario para atender tu vehículo y contactarte al respecto.'
    },
    sections: [{
      heading: {
        en: 'What we collect',
        es: 'Qué recopilamos'
      },
      body: {
        en: 'When you submit an appointment request we collect your name, phone number, optional email address, vehicle details, preferred contact method and language, and the description of the issue you provide. When you visit the shop we keep a service record of the work performed on your vehicle.',
        es: 'Cuando envías una solicitud de cita recopilamos tu nombre, teléfono, correo opcional, datos del vehículo, método de contacto e idioma preferidos, y la descripción del problema que nos das. Cuando visitas el taller guardamos un registro del servicio realizado en tu vehículo.'
      }
    }, {
      heading: {
        en: 'How we use it',
        es: 'Cómo la usamos'
      },
      body: {
        en: 'Your information is used to confirm appointments, contact you about your vehicle, prepare estimates and invoices, and maintain your service history. We do not sell or rent your information to third parties.',
        es: 'Usamos tu información para confirmar citas, contactarte sobre tu vehículo, preparar presupuestos y facturas, y mantener tu historial de servicio. No vendemos ni rentamos tu información a terceros.'
      }
    }, {
      heading: {
        en: 'Who we share it with',
        es: 'Con quién la compartimos'
      },
      body: {
        en: 'We share information only where it is necessary to complete your repair — for example with a parts supplier ordering a component for your vehicle — or where required by law.',
        es: 'Compartimos información solo cuando es necesario para completar tu reparación — por ejemplo con un proveedor al pedir una pieza para tu vehículo — o cuando la ley lo exige.'
      }
    }, {
      heading: {
        en: 'Your choices',
        es: 'Tus opciones'
      },
      body: {
        en: `You may ask us to correct or delete your contact information at any time by calling ${business.phoneDisplay} or emailing ${business.email}. Vehicle service records may be retained as required for warranty purposes.`,
        es: `Puedes pedirnos corregir o eliminar tu información de contacto en cualquier momento llamando al ${business.phoneDisplay} o escribiendo a ${business.email}. Los registros de servicio pueden conservarse por motivos de garantía.`
      }
    }]
  },
  terms: {
    eyebrow: {
      en: 'Terms of Service',
      es: 'Términos de Servicio'
    },
    title: {
      en: 'Working with A&J Auto Repair',
      es: 'Trabajar con A&J Auto Repair'
    },
    description: {
      en: 'The terms that apply to appointment requests made through this website and to work performed at our shop.',
      es: 'Los términos que aplican a las solicitudes de cita hechas en este sitio y al trabajo realizado en nuestro taller.'
    },
    sections: [{
      heading: {
        en: 'Appointment requests',
        es: 'Solicitudes de cita'
      },
      body: {
        en: 'Submitting the form on this website is a request, not a confirmed appointment. An appointment is confirmed only after a member of our team speaks with you. Requested dates and times are subject to availability.',
        es: 'Enviar el formulario de este sitio es una solicitud, no una cita confirmada. Una cita se confirma solo después de que un miembro de nuestro equipo hable contigo. Las fechas y horas solicitadas están sujetas a disponibilidad.'
      }
    }, {
      heading: {
        en: 'Estimates and authorization',
        es: 'Presupuestos y autorización'
      },
      body: {
        en: 'We provide an estimate before beginning work and no repair proceeds without your authorization. If additional work is required, we stop and contact you for approval before continuing. Estimates may change if concealed damage or additional faults are discovered.',
        es: 'Damos un presupuesto antes de empezar y ninguna reparación procede sin tu autorización. Si se requiere trabajo adicional, nos detenemos y te contactamos para aprobación antes de continuar. Los presupuestos pueden cambiar si se descubren daños ocultos o fallas adicionales.'
      }
    }, {
      heading: {
        en: 'Warranty',
        es: 'Garantía'
      },
      body: {
        en: 'Repairs are covered by a parts and labor warranty; the specific coverage depends on the part and its manufacturer and is stated on your invoice. Warranty coverage excludes damage from abuse, accident, neglected maintenance, or work performed elsewhere.',
        es: 'Las reparaciones están cubiertas por una garantía de piezas y mano de obra; la cobertura específica depende de la pieza y su fabricante y se indica en tu factura. La garantía excluye daños por mal uso, accidente, falta de mantenimiento o trabajo realizado en otro lugar.'
      }
    }, {
      heading: {
        en: 'Payment and vehicle pickup',
        es: 'Pago y entrega del vehículo'
      },
      body: {
        en: 'Payment is due in full when you pick up your vehicle. We accept cash and major credit and debit cards. Vehicles left more than 30 days after completion without contact may be subject to storage fees as permitted by Maryland law.',
        es: 'El pago se realiza en su totalidad al recoger el vehículo. Aceptamos efectivo y tarjetas de crédito y débito principales. Los vehículos dejados más de 30 días después de terminados y sin contacto pueden generar cargos de almacenamiento según lo permite la ley de Maryland.'
      }
    }, {
      heading: {
        en: 'Website content',
        es: 'Contenido del sitio'
      },
      body: {
        en: 'Service descriptions, prices shown as "starting at", and maintenance guidance on this site are general information, not a diagnosis or final quote for your specific vehicle. Always confirm intervals and recommendations with us or your owner’s manual.',
        es: 'Las descripciones de servicios, los precios mostrados como “a partir de” y las recomendaciones de mantenimiento en este sitio son información general, no un diagnóstico ni una cotización final para tu vehículo. Confirma siempre los intervalos y recomendaciones con nosotros o con tu manual del propietario.'
      }
    }]
  }
};
export function Legal({
  kind
}: LegalProps) {
  const {
    t
  } = useLanguage();
  const page = content[kind];
  return <>
      <Seo title={`${t(page.eyebrow)} | A&J Auto Repair Havre de Grace, MD`} description={t(page.description)} data-id="element-440" data-mp="2lms9yb3ilj9q" />
      <PageHeader eyebrow={t(page.eyebrow)} title={t(page.title)} description={t(page.description)} data-id="element-441" data-mp="2hiataksgbm36" />
      <section className="bg-white py-14 sm:py-16" data-id="element-442" data-mp="39btdpl1ek68o">
        <div className="mx-auto max-w-3xl space-y-10 px-6" data-id="element-443" data-mp="39ccswtzo4gj0">
          {page.sections.map(section => <div key={section.heading.en} data-id="element-444" data-mp="35c2ovmvfs06s">
              <h2 className="font-display text-xl font-bold text-navy-900" data-id="element-445" data-mp="30beoialbi6zw">
                {t(section.heading)}
              </h2>
              <p className="mt-3 leading-relaxed text-navy-600" data-id="element-446" data-mp="1tvkurtfba9nb">
                {t(section.body)}
              </p>
            </div>)}
          <p className="border-t border-navy-100 pt-8 text-sm text-navy-500" data-id="element-447" data-mp="3oxzbx7ywvcik">
            {business.phoneDisplay} · {business.email} ·{' '}
            {new Date().getFullYear()}
          </p>
        </div>
      </section>
    </>;
}